(() => {
  const PARAM_NAME = 'sa_shared_cart';
  const NATIVE_PENDING_KEY = 'saSharedCartNativePending';
  const IMPORT_NOTICE_KEY = 'saSharedCartImportNotice';
  const MAX_ITEMS = 100;
  const MAX_QUANTITY = 100;
  const MAX_PROPERTIES = 40;
  const MAX_PROPERTY_KEY_LENGTH = 120;
  const MAX_PROPERTY_VALUE_LENGTH = 2000;
  const MAX_PROPERTY_TOTAL_LENGTH = 10000;
  const MAX_PAYLOAD_LENGTH = 12000;
  const MAX_SHARE_URL_LENGTH = 7500;

  class SaShareCart extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.cartJsonUrl = this.dataset.cartJsonUrl || `${this.cartUrl}.js`;
      this.cartAddUrl = this.dataset.cartAddUrl || `${this.cartUrl}/add.js`;
      this.cartClearUrl = this.dataset.cartClearUrl || `${this.cartUrl}/clear.js`;
      this.cartUpdateUrl = this.dataset.cartUpdateUrl || `${this.cartUrl}/update.js`;
      this.canonicalOrigin = this.dataset.canonicalOrigin || window.location.origin;
      this.currency = this.dataset.currency || 'AUD';

      this.shareDialog = this.querySelector('[data-sa-share-cart-dialog]');
      this.sharedDialog = this.querySelector('[data-sa-shared-cart-dialog]');
      this.copyButton = this.querySelector('[data-sa-share-cart-copy]');
      this.nativeShareButton = this.querySelector('[data-sa-share-cart-native]');
      this.shareStatus = this.querySelector('[data-sa-share-cart-status]');
      this.sharedStatus = this.querySelector('[data-sa-shared-cart-status]');
      this.replaceButton = this.querySelector('[data-sa-shared-cart-replace]');

      this.bindEvents();
      this.restoreStoredNotice();
      this.finishNativeImport();
      this.handleIncomingShare();
    }

    bindEvents() {
      this.querySelector('[data-sa-share-cart-open]')?.addEventListener('click', () => this.openShareDialog());
      this.copyButton?.addEventListener('click', () => this.copyShareUrl());
      this.nativeShareButton?.addEventListener('click', () => this.nativeShare());
      this.querySelectorAll('[data-sa-share-cart-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDialog(this.shareDialog));
      });
      this.querySelectorAll('[data-sa-shared-cart-keep]').forEach((button) => {
        button.addEventListener('click', () => this.keepCurrentCart());
      });
      this.replaceButton?.addEventListener('click', () => this.replaceCurrentCart());

      [this.shareDialog, this.sharedDialog].forEach((dialog) => {
        dialog?.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          if (dialog === this.sharedDialog) this.keepCurrentCart();
          else this.closeDialog(dialog);
        });
      });
      this.sharedDialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.keepCurrentCart();
      });
    }

    async openShareDialog() {
      this.setShareStatus('Preparing your cart link...');
      this.copyButton.disabled = true;
      this.nativeShareButton.hidden = true;
      this.showDialog(this.shareDialog);

      try {
        const cart = await this.fetchCart();
        if (!cart.items?.length) throw new Error('Add at least one product before sharing this cart.');

        this.currentShareUrl = this.buildShareUrl(cart);
        this.shareDialog.dataset.shareUrl = this.currentShareUrl;
        this.updateSummary(cart);
        this.copyButton.disabled = false;
        this.nativeShareButton.hidden = typeof navigator.share !== 'function';
        this.setShareStatus('');
      } catch (error) {
        this.currentShareUrl = '';
        delete this.shareDialog.dataset.shareUrl;
        this.setShareStatus(error.message || 'This cart link could not be created.', true);
      }
    }

    async copyShareUrl() {
      if (!this.currentShareUrl) return;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.currentShareUrl);
        } else {
          this.copyWithFallback(this.currentShareUrl);
        }
        this.setShareStatus('Link copied \u2713');
      } catch (error) {
        try {
          this.copyWithFallback(this.currentShareUrl);
          this.setShareStatus('Link copied \u2713');
        } catch (fallbackError) {
          this.setShareStatus('The link could not be copied. Please try again.', true);
        }
      }
    }

    copyWithFallback(value) {
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand('copy');
      input.remove();
      if (!copied) throw new Error('Copy failed');
    }

    async nativeShare() {
      if (!this.currentShareUrl || typeof navigator.share !== 'function') return;
      try {
        await navigator.share({
          title: 'Security Australia cart',
          text: 'Here is the Security Australia product setup we discussed.',
          url: this.currentShareUrl,
        });
        this.setShareStatus('Cart link shared.');
      } catch (error) {
        if (error?.name !== 'AbortError') {
          this.setShareStatus('Sharing is unavailable. Copy the cart link instead.', true);
        }
      }
    }

    buildShareUrl(cart) {
      const items = cart.items.map((item) => this.cleanCartItem(item));
      const needsPropertyPayload = items.some((item) => item.properties || item.selling_plan);
      const token = needsPropertyPayload
        ? `p.${this.encodePayload({ v: 1, items })}`
        : `n.${items.map((item) => `${item.id}:${item.quantity}`).join(',')}`;
      const shareUrl = new URL(this.cartUrl, this.canonicalOrigin);

      shareUrl.searchParams.set(PARAM_NAME, token);
      const discountCode = this.getDiscountCode(cart);
      if (discountCode) shareUrl.searchParams.set('discount', discountCode);

      if (shareUrl.toString().length > MAX_SHARE_URL_LENGTH) {
        throw new Error('This cart contains too many configuration details to create a reliable link. Please contact the customer directly.');
      }
      return shareUrl.toString();
    }

    cleanCartItem(item) {
      const properties = item.properties && Object.keys(item.properties).length
        ? item.properties
        : undefined;
      const cleanItem = this.validateItem({
        id: item.variant_id || item.id,
        quantity: item.quantity,
        properties,
        selling_plan: item.selling_plan_allocation?.selling_plan?.id || undefined,
      });
      return cleanItem;
    }

    getDiscountCode(cart) {
      const code = cart.discount_codes?.find((discount) => discount.applicable !== false)?.code;
      return this.validateDiscountCode(code) ? code : '';
    }

    encodePayload(payload) {
      const json = JSON.stringify(payload);
      if (json.length > MAX_PAYLOAD_LENGTH) throw new Error('The cart configuration is too large to share as a link.');

      const bytes = new TextEncoder().encode(json);
      let binary = '';
      bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    }

    decodePayload(value) {
      try {
        if (!value || value.length > MAX_PAYLOAD_LENGTH * 2) throw new Error();
        const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        const binary = atob(padded);
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        const json = new TextDecoder().decode(bytes);
        if (json.length > MAX_PAYLOAD_LENGTH) throw new Error();
        return JSON.parse(json);
      } catch (error) {
        throw new Error('The shared cart link is invalid or too large.');
      }
    }

    parseShareToken(token) {
      if (!token || token.length > MAX_PAYLOAD_LENGTH * 2) throw new Error('The shared cart link is invalid or too large.');

      if (token.startsWith('n.')) {
        const entries = token.slice(2).split(',');
        if (!entries.length || entries.length > MAX_ITEMS) throw new Error('The shared cart does not contain a valid product setup.');
        const items = entries.map((entry) => {
          const parts = entry.split(':');
          if (parts.length !== 2) throw new Error('The shared cart contains an invalid product.');
          return this.validateItem({ id: parts[0], quantity: parts[1] });
        });
        return { mode: 'native', items };
      }

      if (token.startsWith('p.')) {
        const payload = this.decodePayload(token.slice(2));
        if (!payload || payload.v !== 1 || !Array.isArray(payload.items)) {
          throw new Error('The shared cart link uses an unsupported format.');
        }
        if (!payload.items.length || payload.items.length > MAX_ITEMS) {
          throw new Error('The shared cart does not contain a valid product setup.');
        }
        return { mode: 'properties', items: payload.items.map((item) => this.validateItem(item)) };
      }

      throw new Error('The shared cart link uses an unsupported format.');
    }

    validateItem(item) {
      const idText = String(item?.id ?? '');
      const quantityText = String(item?.quantity ?? '');
      if (!/^\d{1,20}$/.test(idText)) throw new Error('The shared cart contains an invalid product ID.');
      if (!/^\d{1,3}$/.test(quantityText)) throw new Error('The shared cart contains an invalid quantity.');

      const id = Number(idText);
      const quantity = Number(quantityText);
      if (!Number.isSafeInteger(id) || id <= 0) throw new Error('The shared cart contains an invalid product ID.');
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) {
        throw new Error(`Shared cart quantities must be between 1 and ${MAX_QUANTITY}.`);
      }

      const cleanItem = { id, quantity };
      if (item.selling_plan !== undefined) {
        const sellingPlanText = String(item.selling_plan);
        const sellingPlan = Number(sellingPlanText);
        if (!/^\d{1,20}$/.test(sellingPlanText) || !Number.isSafeInteger(sellingPlan) || sellingPlan <= 0) {
          throw new Error('The shared cart contains an invalid selling plan.');
        }
        cleanItem.selling_plan = sellingPlan;
      }

      if (item.properties !== undefined && item.properties !== null) {
        cleanItem.properties = this.validateProperties(item.properties);
      }
      return cleanItem;
    }

    validateProperties(properties) {
      if (typeof properties !== 'object' || Array.isArray(properties)) {
        throw new Error('The shared cart contains invalid product selections.');
      }

      const entries = Object.entries(properties);
      if (entries.length > MAX_PROPERTIES) throw new Error('A shared product contains too many selections.');

      const cleanProperties = Object.create(null);
      let totalLength = 0;
      entries.forEach(([key, value]) => {
        if (typeof value !== 'string' || !key || key.length > MAX_PROPERTY_KEY_LENGTH || value.length > MAX_PROPERTY_VALUE_LENGTH) {
          throw new Error('The shared cart contains an invalid product selection.');
        }
        totalLength += key.length + value.length;
        if (totalLength > MAX_PROPERTY_TOTAL_LENGTH) throw new Error('A shared product configuration is too large.');
        cleanProperties[key] = value;
      });
      return cleanProperties;
    }

    async handleIncomingShare() {
      const currentUrl = new URL(window.location.href);
      const token = currentUrl.searchParams.get(PARAM_NAME);
      if (!token) return;

      try {
        this.pendingShare = this.parseShareToken(token);
        this.pendingDiscount = this.validateDiscountCode(currentUrl.searchParams.get('discount'))
          ? currentUrl.searchParams.get('discount')
          : '';
        const cart = await this.fetchCart();

        if (cart.item_count > 0) {
          this.showSharedCartConfirmation();
        } else {
          await this.applyPendingShare(false);
        }
      } catch (error) {
        this.showSharedCartError(error.message || 'This shared cart link is invalid.');
      }
    }

    showSharedCartConfirmation() {
      this.querySelector('[data-sa-shared-cart-title]').textContent = 'Open shared cart?';
      this.querySelector('[data-sa-shared-cart-copy]').textContent = 'This link contains a Security Australia product setup. Replace your current cart with the shared setup?';
      this.replaceButton.hidden = false;
      this.replaceButton.disabled = false;
      this.querySelectorAll('[data-sa-shared-cart-keep]').forEach((button) => { button.disabled = false; });
      this.setSharedStatus('');
      this.showDialog(this.sharedDialog);
    }

    showSharedCartError(message) {
      this.querySelector('[data-sa-shared-cart-title]').textContent = 'Shared cart link problem';
      this.querySelector('[data-sa-shared-cart-copy]').textContent = 'We could not safely open this product setup. Your current cart has not been changed.';
      this.replaceButton.hidden = true;
      this.setSharedStatus(message, true);
      this.showDialog(this.sharedDialog);
    }

    async replaceCurrentCart() {
      if (!this.pendingShare) return;
      this.replaceButton.disabled = true;
      this.querySelectorAll('[data-sa-shared-cart-keep]').forEach((button) => { button.disabled = true; });
      this.setSharedStatus('Replacing your cart...');

      try {
        await this.postJson(this.cartClearUrl, {});
        await this.applyPendingShare(true);
      } catch (error) {
        this.replaceButton.disabled = false;
        this.querySelectorAll('[data-sa-shared-cart-keep]').forEach((button) => { button.disabled = false; });
        this.setSharedStatus(error.message || 'The shared cart could not be opened.', true);
      }
    }

    keepCurrentCart() {
      this.cleanSharedUrl();
      this.pendingShare = null;
      this.closeDialog(this.sharedDialog);
    }

    async applyPendingShare(cartWasCleared) {
      if (!this.pendingShare) return;

      if (this.pendingShare.mode === 'native') {
        sessionStorage.setItem(NATIVE_PENDING_KEY, 'true');
        window.location.replace(this.buildNativePermalink(this.pendingShare.items, window.location.origin, this.pendingDiscount));
        return;
      }

      if (!cartWasCleared) {
        const cart = await this.fetchCart();
        if (cart.item_count > 0) throw new Error('Your current cart must be confirmed before opening this shared setup.');
      }

      const failures = [];
      for (const item of this.pendingShare.items) {
        try {
          await this.postJson(this.cartAddUrl, { items: [item] });
        } catch (error) {
          failures.push(`Variant ${item.id}: ${error.message || 'could not be added'}`);
        }
      }

      await this.markCartAsShared();
      if (failures.length) {
        sessionStorage.setItem(IMPORT_NOTICE_KEY, `Some shared products could not be added. ${failures.join(' ')}`);
      }
      window.location.replace(this.cartDestination(this.pendingDiscount));
    }

    buildNativePermalink(items, origin, discountCode) {
      const entries = items.map((item) => `${item.id}:${item.quantity}`).join(',');
      const permalink = new URL(`${this.cartUrl.replace(/\/$/, '')}/${entries}`, origin);
      permalink.searchParams.set('storefront', 'true');
      if (discountCode) permalink.searchParams.set('discount', discountCode);
      return permalink.toString();
    }

    cartDestination(discountCode) {
      const destination = new URL(this.cartUrl, window.location.origin);
      if (discountCode) destination.searchParams.set('discount', discountCode);
      return destination.toString();
    }

    async finishNativeImport() {
      if (sessionStorage.getItem(NATIVE_PENDING_KEY) !== 'true') return;
      sessionStorage.removeItem(NATIVE_PENDING_KEY);
      try {
        await this.markCartAsShared();
      } catch (error) {
        console.warn('Shared cart attribution could not be saved.', error);
      }
      if (!new URL(window.location.href).searchParams.has(PARAM_NAME)) this.cleanSharedUrl();
    }

    async markCartAsShared() {
      await this.postJson(this.cartUpdateUrl, { attributes: { __sa_shared_cart: 'true' } });
    }

    cleanSharedUrl() {
      window.history.replaceState({}, '', this.cartUrl);
    }

    restoreStoredNotice() {
      const message = sessionStorage.getItem(IMPORT_NOTICE_KEY);
      if (!message) return;
      sessionStorage.removeItem(IMPORT_NOTICE_KEY);
      const target = document.getElementById('cart-errors');
      if (target) {
        target.textContent = message;
        target.classList.add('sa-share-cart__import-error');
      }
    }

    async fetchCart() {
      const response = await fetch(this.cartJsonUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('The cart could not be read. Please refresh and try again.');
      return response.json();
    }

    async postJson(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status >= 400) {
        throw new Error(result.description || result.message || 'This product is unavailable.');
      }
      return result;
    }

    updateSummary(cart) {
      const count = Number(cart.item_count) || 0;
      const countNode = this.querySelector('[data-sa-share-cart-count]');
      const subtotalNode = this.querySelector('[data-sa-share-cart-subtotal]');
      if (countNode) countNode.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
      if (subtotalNode) subtotalNode.textContent = new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: cart.currency || this.currency,
      }).format((Number(cart.total_price) || 0) / 100) + ` ${cart.currency || this.currency}`;
    }

    validateDiscountCode(code) {
      return typeof code === 'string' && /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(code);
    }

    setShareStatus(message, isError = false) {
      if (!this.shareStatus) return;
      this.shareStatus.textContent = message;
      this.shareStatus.classList.toggle('is-error', isError);
    }

    setSharedStatus(message, isError = false) {
      if (!this.sharedStatus) return;
      this.sharedStatus.textContent = message;
      this.sharedStatus.classList.toggle('is-error', isError);
    }

    showDialog(dialog) {
      if (!dialog) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }

    closeDialog(dialog) {
      if (!dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }

  if (!customElements.get('sa-share-cart')) {
    customElements.define('sa-share-cart', SaShareCart);
  }
})();
